import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";
import WorkerEventsTable from "../components/WorkerEventsTable";
import { Link } from "react-router-dom";
import { 
  MagnifyingGlassIcon,
  StarIcon,
  TagIcon,
  TicketIcon,
  MusicalNoteIcon,
  UserGroupIcon 
} from "@heroicons/react/24/solid";
import { SERVER_BASE_URL } from "../config";

// Helper functions
const isPastEvent = (startTime) => {
  if (!startTime) return false;
  return new Date(startTime) < new Date();
};

const isOngoingEvent = (eventStartTime, eventEndTime) => {
  if (!eventStartTime || !eventEndTime) {
    return false;
  }
  const startTimeDate = new Date(eventStartTime);
  const endTimeDate = new Date(eventEndTime);
  const now = new Date();
  return now > startTimeDate && now < endTimeDate;
};

function WorkerDashboard() {
  // console.log("===== RENDER WorkerDashboard =====");
  const { user, isAuthenticated, isInitializing } = useAuth();
  const [events, setEvents] = useState([]);
  const [ongoingEventsForDisplay, setOngoingEventsForDisplay] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [error, setError] = useState(null);

  // Sorting and search states
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");

  // Function to render category icon (similar to GuestListPage)
  const renderCategoryIcon = (category) => {
    switch (category) {
      case "VIP":
        return <StarIcon className="h-4 w-4 text-yellow-500" title="VIP" />;
      case "BACKSTAGE":
        return <TagIcon className="h-4 w-4 text-purple-500" title="Backstage" />;
      case "ARTIST":
        return <MusicalNoteIcon className="h-4 w-4 text-purple-500" title="Artist" />;
      case "STAFF":
        return <UserGroupIcon className="h-4 w-4 text-purple-500" title="Staff" />;
      case "REGULAR":
      default:
        return <TicketIcon className="h-4 w-4 text-gray-400" title="Regular" />;
    }
  };

  const loadAssignedEvents = useCallback(async () => {
    // console.log("Function loadAssignedEvents called");
    if (!user || !isAuthenticated) {
      console.log("User is not authenticated or missing");
      setLoading(false);
      return;
    }
    try {
      // console.log("Sending API request to /api/workers/me/events");
      const res = await fetch(`${SERVER_BASE_URL}/api/workers/me/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // console.log("API response status:", res.status);
      if (!res.ok) {
        const errorData = await res.json();
        console.log("Error data from API:", errorData);
        throw new Error(errorData.detail || "Failed to load assigned events");
      }
      const data = await res.json();
      // console.log("Received events:", data);
      // console.log("User role:", user?.role);

      // === Modified Filtering Logic ===
      const ongoingEvents = data.filter(event => isOngoingEvent(event.start_time, event.end_time));
      const notOngoingEvents = data.filter(event => !isOngoingEvent(event.start_time, event.end_time));
      const futureNotOngoingEvents = notOngoingEvents.filter(event => !isPastEvent(event.start_time));

      setOngoingEventsForDisplay(ongoingEvents);
      setEvents(futureNotOngoingEvents);

    } catch (error) {
      console.error("Error loading events:", error);
      toast.error(error.message);
      setError(error.message);
    } finally {
      setLoading(false);
      // console.log("Loading completed");
    }
  }, [token, user, isAuthenticated]);

  useEffect(() => {
    // console.log("useEffect: Initializing event loading");
    loadAssignedEvents();
  }, [loadAssignedEvents]);

  // Sorting function
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? "▲" : "▼";
  };

  // Sort events (sorting is still applied to the filtered list)
  const sortedEvents = [...events].sort((a, b) => {
    let compA, compB;
    if (sortBy === "state") {
      compA = a.state ? a.state.toLowerCase() : "";
      compB = b.state ? b.state.toLowerCase() : "";
    } else if (sortBy === "name") {
      compA = a.name ? a.name.toLowerCase() : "";
      compB = b.name ? b.name.toLowerCase() : "";
    } else if (sortBy === "date") {
      compA = a.start_time ? new Date(a.start_time).getTime() : 0;
      compB = b.start_time ? new Date(b.start_time).getTime() : 0;
    }
    if (compA < compB) return sortOrder === "asc" ? -1 : 1;
    if (compA > compB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Filter events by search query (by name and state) - applied after filtering past events
  const filteredEvents = sortedEvents.filter((event) => {
    const query = searchQuery.toLowerCase();
    return (
      event.name.toLowerCase().includes(query) ||
      (event.state && event.state.toLowerCase().includes(query))
    );
  });

  if (isInitializing || loading) {
    return (
      <div className="container mx-auto max-w-5xl p-4 mt-16 flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto max-w-5xl p-4 mt-16">
        <p>Please log in to view your assigned events.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl p-4 mt-16">
      <h1 className="text-3xl font-bold mb-4 text-center">
        Hello {user?.name ? `, ${user.name}!` : "Andon!"}
      </h1>
      <p className="text-center text-gray-600 mb-4">
        Here you can view the events assigned to you.
      </p>

      {error && (
        <div className="text-center text-red-500 mb-4">
          <p>Error: {error}</p>
        </div>
      )}

      <div className="mb-4 flex justify-center">
        <input
          type="text"
          placeholder="Search..."
          className="p-2 border rounded-l w-full max-w-md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button className="bg-gray-100 border border-l-0 rounded-r p-2" onClick={() => {}}>
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {ongoingEventsForDisplay.length > 0 && (
        <div className="mb-8 rounded-lg shadow-md">
          <p className="font-semibold text-gray-700 mb-1 px-4 py-2 bg-white rounded-t-lg">Ongoing Events</p>
          <div>
            {ongoingEventsForDisplay.map((event) => (
              <Link
                key={event.id}
                to={`/worker/events/${event.id}/guest-list`}
                className={`
                  grid
                  grid-cols-[1fr_4fr_2fr]
                  items-center
                  bg-green-50
                  border-b border-green-200
                  divide-x divide-gray-200
                  p-2
                  hover:bg-green-100
                  transition duration-200
                  cursor-pointer
                  text-sm
                `}
              >
                <div className="flex items-center space-x-2 px-2">
                  <span className={`w-2 h-2 rounded bg-green-500`} />
                  <span className="text-sm">ongoing</span>
                </div>
                <div className="text-base font-medium text-black px-2 flex items-center">
                  {event.category && renderCategoryIcon(event.category)}
                  <span className="ml-1">{event.name}</span>
                </div>
                <div className="text-sm font-medium text-gray-600 text-center px-2">
                  {new Date(event.start_time).toLocaleDateString("en-US", {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {filteredEvents.length === 0 ? (
        <div className="text-center">
          <p>No upcoming assigned events.</p>
        </div>
      ) : (
        <div className="rounded-lg shadow-md">
          <WorkerEventsTable
            events={filteredEvents}
            onSort={handleSort}
            renderSortIcon={renderSortIcon}
            renderCategoryIcon={renderCategoryIcon}
          />
        </div>
      )}
    </div>
  );
}

export default WorkerDashboard;